import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import api from 'api/client';

const actionButtonSx = {
  bgcolor: 'var(--fz-surface-1, #111827)',
  color: '#f59e0b',
  border: '1px solid #f59e0b',
  '&:hover': { bgcolor: 'var(--fz-surface-0, #0b1220)', borderColor: '#f59e0b' }
};

function parseList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function parseError(error, fallback) {
  const payload = error?.response?.data;
  if (!payload) return error?.message || fallback;
  if (typeof payload === 'string') return payload;
  if (payload.detail) return payload.detail;
  const first = Object.keys(payload)[0];
  if (first) {
    const value = payload[first];
    if (Array.isArray(value)) return value[0];
    if (typeof value === 'string') return value;
  }
  return fallback;
}

const AdminUserAssignmentDialog = ({
  open,
  endpoint,
  title,
  subtitle,
  roleOptions = [],
  defaultRole = '',
  onClose,
  onSaved
}) => {
  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedRole, setSelectedRole] = useState(defaultRole || roleOptions?.[0]?.value || '');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !endpoint) return;
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [usersRes, currentRes] = await Promise.all([api.get('/api/admin/users_lookup/'), api.get(endpoint)]);
        if (!active) return;
        const userRows = parseList(usersRes.data);
        const assignmentRows = parseList(currentRes.data);
        setUsers(userRows);
        setSelectedIds(
          assignmentRows
            .map((item) => Number(item.user))
            .filter((id) => Number.isFinite(id) && id > 0)
        );
      } catch (err) {
        if (!active) return;
        setError(parseError(err, 'Failed to load users or assignments.'));
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [open, endpoint]);

  useEffect(() => {
    if (!open) {
      setError('');
      setSaving(false);
      setLoading(false);
      setSelectedIds([]);
      setUsers([]);
      setSelectedRole(defaultRole || roleOptions?.[0]?.value || '');
    }
  }, [open, defaultRole, roleOptions]);

  const selectedUsers = useMemo(
    () => users.filter((user) => selectedIds.includes(Number(user.id))),
    [users, selectedIds]
  );

  const handleSave = async () => {
    if (!endpoint) return;
    setSaving(true);
    setError('');
    try {
      const payload = { user_ids: selectedIds };
      if (roleOptions.length > 0 && selectedRole) payload.role = selectedRole;
      await api.post(endpoint, payload);
      if (onSaved) await onSaved();
      if (onClose) onClose();
    } catch (err) {
      setError(parseError(err, 'Failed to save assignments.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => (saving ? null : onClose?.())} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Autocomplete
            multiple
            options={users}
            value={selectedUsers}
            loading={loading}
            disableCloseOnSelect
            getOptionLabel={(option) => option.email || option.username || `User #${option.id}`}
            onChange={(_, next) => {
              setSelectedIds(next.map((item) => item.id));
            }}
            renderInput={(params) => <TextField {...params} label="Assigned users" placeholder="Select users" />}
          />

          {roleOptions.length > 0 ? (
            <TextField
              select
              label="Role for selected users"
              value={selectedRole}
              onChange={(event) => setSelectedRole(event.target.value)}
            >
              {roleOptions.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.()} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading} sx={actionButtonSx}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminUserAssignmentDialog;
